import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import {
  BarChart3,
  BookOpen,
  Check,
  Cpu,
  RefreshCw,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { Badge } from "./Badge";
import markezyLogo from "../../markezy-login-logo.png";

const INK = "#171b2b";
const MUTED = "#657087";
const PINK = "#e6007e";

const FEATURES = [
  { icon: UserPlus, title: "Clientes y proyectos", description: "Organiza la operativa de cada cuenta." },
  { icon: Cpu, title: "IA aplicada al trabajo", description: "Copiloto y herramientas para avanzar." },
  { icon: BookOpen, title: "Contenidos e informes", description: "Planifica, crea y comparte con tus clientes." },
  { icon: BarChart3, title: "Visibilidad y analítica", description: "Datos SEO y métricas en contexto." },
];

const OFFER = [
  { label: "Durante 15 días", title: "Todas las funcionalidades", description: "Prueba el potencial completo de Markezy desde el primer día." },
  { label: "Después, si quieres", title: "3 clientes gratis para siempre", description: "Sin fecha límite. Sin tarjeta. Sin cargos automáticos.", free: true },
];

const TRUST = ["Sin tarjeta de crédito", "Sin cobros sorpresa", "Alta en pocos minutos"];

const PROOF = [
  { icon: ShieldCheck, text: "Conexión protegida" },
  { icon: RefreshCw, text: "Copias de seguridad cifradas" },
];

const pageWidth = { width: { xs: "calc(100% - 36px)", sm: "min(1200px, calc(100% - 56px))" }, mx: "auto" };

function Intro() {
  return (
    <Box component="section" aria-labelledby="auth-hero-title" sx={{ py: 1 }}>
      <Badge color="active">Empieza sin tarjeta y sin compromiso</Badge>

      <Typography component="h1" id="auth-hero-title" sx={{ fontSize: { xs: 36, md: "clamp(38px, 4.3vw, 55px)" }, lineHeight: 1.06, letterSpacing: { xs: "-1.9px", md: "-2.6px" }, fontWeight: 800, color: INK, mt: { xs: 2.25, md: 3 }, mb: 2, maxWidth: 600 }}>
        Prueba Markezy con todo.
        <br />
        <Box component="span" sx={{ backgroundImage: `linear-gradient(90deg, ${PINK}, #f06d42 75%, #f2940d)`, color: "transparent", backgroundClip: "text", WebkitBackgroundClip: "text" }}>
          Quédate gratis.
        </Box>
      </Typography>

      <Typography sx={{ fontSize: { xs: 14, md: 16 }, lineHeight: 1.65, color: "#5f687a", maxWidth: 590 }}>
        Organiza clientes, proyectos, contenidos, conversaciones e informes desde un solo lugar. Descubre cómo sería llevar tu agencia con menos herramientas y más control.
      </Typography>

      <Box aria-label="Tu oferta de registro" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, my: { xs: 2.5, md: 3.5 }, border: "1px solid #f4d2e2", borderRadius: "16px", backgroundColor: "#fff", boxShadow: "0 8px 28px rgba(230, 0, 126, .04)", overflow: "hidden", maxWidth: 590 }}>
        {OFFER.map((step, index) => (
          <Box key={step.label} sx={{ px: 2.25, py: 2, ...(index > 0 && { borderLeft: { sm: "1px solid #f4d2e2" }, borderTop: { xs: "1px solid #f4d2e2", sm: 0 }, backgroundImage: "linear-gradient(135deg, #fff7fb, #fff)" }) }}>
            <Badge color={step.free ? "done" : "active"}>{step.label}</Badge>
            <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: "-.25px", mt: 1, mb: 0.5, color: step.free ? "#087655" : INK }}>{step.title}</Typography>
            <Typography sx={{ fontSize: 12, color: "#6e7789", lineHeight: 1.45 }}>{step.description}</Typography>
          </Box>
        ))}
      </Box>

      <Box aria-label="Funciones incluidas" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: { xs: "14px 10px", sm: "15px 22px" }, maxWidth: 580 }}>
        {FEATURES.map(({ icon: Icon, title, description }) => (
          <Box key={title} sx={{ display: "flex", gap: { xs: 1, sm: 1.5 }, alignItems: "flex-start" }}>
            <Box sx={{ flex: "0 0 31px", width: 31, height: 31, border: "1px solid #f7d3e5", backgroundColor: "#fff8fb", color: PINK, borderRadius: "10px", display: "grid", placeItems: "center" }}>
              <Icon size={16} strokeWidth={1.8} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, mb: 0.5 }}>{title}</Typography>
              <Typography sx={{ fontSize: 11.5, color: "#758094", lineHeight: 1.45 }}>{description}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: "11px 17px", flexWrap: "wrap", mt: 3.5, color: MUTED, fontSize: 11 }}>
        {TRUST.map((text) => (
          <Box key={text} component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Check size={13} strokeWidth={2.4} color="#18805c" />{text}</Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Layout de las pantallas públicas de acceso (login, registro, verificación,
 * recuperación de contraseña): logo arriba, presentación a la izquierda y
 * tarjeta con el formulario a la derecha.
 */
export function AuthLayout({ kicker, title, subtitle, footer, children }) {
  return (
    // En escritorio body y #root son overflow:hidden (index.css); en móvil scrollea el documento
    <Box sx={{ height: { xs: "auto", md: "100vh" }, overflowY: { xs: "visible", md: "auto" } }}>
      <Box sx={{ minHeight: "100vh", position: "relative", overflow: "hidden", color: INK, background: "radial-gradient(ellipse at 4% 5%, #fff0f7 0, transparent 30%), #fff" }}>
        <Box component="header" sx={{ ...pageWidth, height: { xs: 72, sm: 86 }, display: "flex", alignItems: "center" }}>
          <Link component={RouterLink} to="/login" aria-label="Markezy, inicio" sx={{ display: "inline-flex" }}>
            <Box component="img" src={markezyLogo} alt="Markezy — Tu agencia IA Ready. En un solo lugar" sx={{ height: { xs: 40, sm: 48 }, width: "auto", display: "block" }} />
          </Link>
        </Box>

        <Box component="main" sx={{ ...pageWidth, mt: { xs: 1.5, md: 3.5 }, pb: { xs: 4.5, md: 7.5 }, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.04fr) minmax(430px, .86fr)" }, gap: { xs: 4, md: 9.5 }, alignItems: "center", maxWidth: { xs: 700, md: "none" } }}>
          <Box sx={{ order: { xs: 2, md: 1 } }}>
            <Intro />
          </Box>

          <Box component="section" sx={{ order: { xs: 1, md: 2 }, position: "relative", "&::before": { content: '""', position: "absolute", zIndex: 0, inset: "-40px -45px", background: "radial-gradient(ellipse, #fbe4f0 0, transparent 68%)", filter: "blur(4px)" } }}>
            <Box sx={{ position: "relative", zIndex: 1, backgroundColor: "#fff", border: "1px solid #f0edf1", borderRadius: { xs: "17px", sm: "22px" }, px: { xs: 2.5, sm: 4.25 }, pt: { xs: 3, sm: 3.75 }, pb: { xs: 2.75, sm: 3.25 }, boxShadow: "0 24px 70px rgba(49, 39, 131, .07)", maxWidth: { xs: "100%", md: 465 }, mx: "auto" }}>
              {kicker && <Badge color="active">{kicker}</Badge>}
              <Typography component="h2" sx={{ fontSize: { xs: 23, sm: 25 }, fontWeight: 800, letterSpacing: "-.75px", color: INK, mt: kicker ? 1.75 : 0, mb: 0.75 }}>
                {title}
              </Typography>
              {subtitle && <Typography sx={{ fontSize: 13, color: "#748094", lineHeight: 1.5 }}>{subtitle}</Typography>}

              <Box sx={{ mt: 2.5 }}>{children}</Box>

              {footer && (
                <Box sx={{ borderTop: "1px solid #f0eef2", mt: 2.5, pt: 2, textAlign: "center", color: "#697286", fontSize: 12.5, "& a": { color: PINK, fontWeight: 750, textDecoration: "none" } }}>
                  {footer}
                </Box>
              )}
            </Box>

            <Box sx={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "center", gap: "9px 18px", flexWrap: "wrap", mt: 2, color: "#687386", fontSize: 11 }}>
              {PROOF.map(({ icon: Icon, text }) => (
                <Box key={text} component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Icon size={14} strokeWidth={1.8} color="#17815d" />{text}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box component="footer" sx={{ borderTop: "1px solid #f0edf1", backgroundColor: "#fffafd", px: { xs: 2.25, sm: 3.5, lg: "calc((100% - 1200px) / 2)" }, pt: { xs: 2.5, sm: 3.5 }, pb: { xs: 3.5, sm: 4.5 }, display: { xs: "block", sm: "flex" }, justifyContent: "space-between", alignItems: "flex-start", gap: 4 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.75, color: INK }}>Tus datos y la IA, con criterio.</Typography>
            <Typography sx={{ fontSize: 11.5, lineHeight: 1.6, color: "#778094", maxWidth: 700 }}>
              La plataforma utiliza conexión cifrada y copias de seguridad diarias cifradas y replicadas fuera del servidor. En las funciones de IA, apostamos por la transparencia, la supervisión humana y el control por parte de las personas, en línea con los principios del AI Act.
            </Typography>
          </Box>
          <Link component={RouterLink} to="/privacidad" sx={{ display: "inline-block", mt: { xs: 1.25, sm: 0 }, color: "#8b6077", fontSize: 11.5, textDecoration: "none", whiteSpace: "nowrap" }}>
            Política de privacidad
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

AuthLayout.propTypes = {
  kicker: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
};
